// MUI-free copy of Ui-components/platforms.
import React, {memo} from 'react';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import FadeInUp from '@/components/atoms/FadeInUp';
import styles from './Platforms.module.scss';

export interface PlatformsCard {
    icon: StaticImageData;
    heading: string;
    body?: string;
    redirect: string;
}

interface PlatformsProps {
    data?: PlatformsCard[];
    heading: React.ReactNode;
    subheading?: string;
    /** Global class(es) added to every card (e.g. `platform-key-industry-service no-border`). */
    className?: string;
    /** Accepted for API parity with the original; it never rendered anything. */
    containerClass?: string;
    showBorder?: boolean;
    /**
     * Replaces the original MUI `cardSx`: a (CSS-module) class applied to the same card element. It outranks the
     * card's base styles regardless of stylesheet order, so no `!important` is needed.
     */
    cardClassName?: string;
}

const BORDER_STYLE: React.CSSProperties = {border: '2px solid #F5F5F9', borderRadius: '15px'}; // raw values kept from the original

const Platforms = ({data, heading, subheading, className, showBorder, cardClassName}: PlatformsProps) => {
    const cardClass = [styles.card, className, cardClassName].filter(Boolean).join(' ');

    return (
        <div className="one-step-component-wrapper">
            <div className={styles.header}>
                <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                {subheading && <p className={`font-regular-text  remove-underline ${styles.subheading}`}>{parse(subheading)}</p>}
            </div>
            <div className={`business-grid-wrapper ${styles.grid}`}>
                {data?.map((subData, index) => (
                    <div className={styles.item} key={index + 1}>
                        <FadeInUp className="height-full">
                            <div className={cardClass} style={showBorder ? BORDER_STYLE : undefined}>
                                <div>
                                    <OptimizedImage src={subData.icon} alt={subData.heading} priority={false} className="step-image" />
                                </div>
                                <div className={styles.body}>
                                    {subData.redirect !== '' ? (
                                        <Link
                                            href={subData?.redirect}
                                            target="_blank"
                                            className="services_link menu-links hover-underline-text"
                                        >
                                            <h3 className={`one-step-content-heading  ${styles.title}`}>{subData.heading}</h3>
                                        </Link>
                                    ) : (
                                        <h3 className={`one-step-content-heading  ${styles.title}`}>{subData.heading}</h3>
                                    )}
                                    <p className={`one-step-sub-heading  remove-underline  ${styles.text}`}>
                                        {subData.body ? parse(subData.body) : ''}
                                    </p>
                                </div>
                            </div>
                        </FadeInUp>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default memo(Platforms);
